import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { IRefRegisterAddressData, IRegisterAddressData } from 'entities';
import { IAddressViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { MASK_CEP, MASK_NUMBER_ADDRESS, UF_CODE_BY_NAME } from '../../constants';
import { useInformationBase, useRegisterForm } from '../../context';
import { getCodeByName } from '../../services/api/ibge';
import { slugify } from '../../utils/slugify';
import { toCnpjMask } from '../../utils/toCnpjMask';
import { Input } from '../Inputs/Input';
import { Container, Content, ContentSummary } from './styles';
import { unMask } from '../../utils/removeMask';
import { useAlert } from '../../hooks/useAlert';
import InformationBaseZipCodeController from '../../controller/InformationBaseZipCodeController';

export const RegisterAddress = forwardRef<IRefRegisterAddressData>((_, ref) => {
  const { dataInformationBaseLegalPerson } = useInformationBase();
  const alert = useAlert();
  const refComponent = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const theme = useTheme();
  const color =
    location.pathname === '/naturalPersonAccount' || location.pathname === '/legalPersonAccount'
      ? 'background'
      : 'background_white';
  const [newAddress, setNewAddress] = useState<IAddressViewModel>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchingCnpjData, setIsSearchingCnpjData] = useState(false);
  const [displaySummary, setDisplaySummary] = useState(false);
  const { setActiveForm, activeForm, tradingName, document, address, formSubmited } =
    useRegisterForm();
  const [defaultValues] = useState({
    name: tradingName,
    postalCode: address?.postalCode?.replace(/\D/g, '') ?? '',
    street: address?.street?.trim() ?? '',
    number: address?.number?.toString() ?? '',
    complement: address?.complement ?? '',
    city: address?.city ?? '',
    uf: address?.uf ?? '',
  });
  const {
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const data = watch();
  const handleHiddenSummary = () => {
    setDisplaySummary(!displaySummary);
    setActiveForm('registerAddress');
  };

  const checkHasAddressError = useCallback(() => {
    let hasAddressError = false;
    if (!data.postalCode) {
      setError('postalCode', { type: 'manual', message: 'Campo obrigatório' });
      hasAddressError = true;
    } else if (data.postalCode.length < 8) {
      setError('postalCode', { type: 'manual', message: 'CEP inválido' });
      hasAddressError = true;
    } else {
      clearErrors('postalCode');
    }
    if (!data.street) {
      setError('street', { type: 'manual', message: 'Campo obrigatório' });
      hasAddressError = true;
    } else {
      clearErrors('street');
    }
    if (!data.number) {
      setError('number', { type: 'manual', message: 'Campo obrigatório' });
      hasAddressError = true;
    } else {
      clearErrors('number');
    }
    if (!data.city) {
      setError('city', { type: 'manual', message: 'Campo obrigatório' });
      hasAddressError = true;
    } else {
      clearErrors('city');
    }
    if (!data.uf) {
      setError('uf', { type: 'manual', message: 'Campo obrigatório' });
      hasAddressError = true;
    } else {
      clearErrors('uf');
    }

    return hasAddressError;
  }, [clearErrors, data.city, data.number, data.postalCode, data.street, data.uf, setError]);

  const handleChangePostalCode = async (postalCode: string) => {
    const postalCodeWithoutMask = unMask(postalCode);
    if (postalCodeWithoutMask.length === 8 && postalCodeWithoutMask !== address.postalCode) {
      setValue('city', '');
      setValue('street', '');
      setValue('uf', '');
      setValue('number', '');
      setValue('complement', '');
      setIsLoading(true);
      setIsSearchingCnpjData(true);
      await InformationBaseZipCodeController.getZipCodeWithGeoLocation(postalCodeWithoutMask)
        .then(async (res) => {
          const addressWithDataIbge = await getCodeByName(slugify(res.city), slugify(res.uf));
          if (!addressWithDataIbge) {
            alert.show({ message: 'CEP não encontrado', type: 'error' });
            setIsLoading(false);
            setIsSearchingCnpjData(false);
            return;
          }
          const newAddressData: IAddressViewModel = {
            city: res.city,
            cityCode: Number(addressWithDataIbge.id),
            postalCode: postalCodeWithoutMask,
            street: res.street.trim(),
            uf: res.uf,
            ufCode: addressWithDataIbge.ufCode,
            latitude: res.latitude ?? 0,
            longitude: res.longitude ?? 0,
          };
          setNewAddress(newAddressData);
          setValue('city', newAddressData?.city ?? '');
          setValue('street', newAddressData?.street?.trim() ?? '');
          setValue('uf', addressWithDataIbge.uf);
          setValue('number', '');
          setValue('complement', '');
        })
        .catch(() => {
          alert.show({ message: 'CEP não encontrado', type: 'error' });
        })
        .finally(() => {
          const timer = setTimeout(() => {
            setIsLoading(false);
            setIsSearchingCnpjData(false);
            clearTimeout(timer);
          }, 250);
        });
    }
  };

  const handleUpdateAddressData = () => {
    const newData: IRegisterAddressData = {
      address: newAddress
        ? {
            ...newAddress,
            number: Number(data.number),
            complement: data.complement,
          }
        : address,
      tradingName: data.name,
      hasError: checkHasAddressError(),
    };
    return newData;
  };

  const handleFocusOnError = () => {
    refComponent.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTourDefaultValues = () => {
    setDisplaySummary(true);
  };

  useImperativeHandle(ref, () => ({
    handleTourDefaultValues,
    handleUpdateAddressData,
    handleFocusOnError,
  }));

  useEffect(() => {
    setValue('number', address.number?.toString() ?? '');
    setValue('city', address?.city ?? '');
    setValue('complement', address?.complement ?? '');
    setValue('street', address?.street?.trim() ?? '');
    setValue('postalCode', address?.postalCode?.replace(/\D/g, '') ?? '');
    setValue('uf', address?.uf ?? '');
    setValue('name', tradingName);
  }, [setValue, tradingName, address]);

  useEffect(() => {
    if (activeForm === 'registerAddress') {
      setDisplaySummary(true);
    } else {
      setDisplaySummary(false);
    }
  }, [activeForm]);

  useEffect(() => {
    checkHasAddressError();
  }, [data.number, data.city, data.street, data.uf, data.postalCode, checkHasAddressError]);

  useEffect(() => {
    if (dataInformationBaseLegalPerson) {
      setNewAddress({
        city: dataInformationBaseLegalPerson?.address?.city,
        cityCode: dataInformationBaseLegalPerson?.address.codeIBGE,
        postalCode: dataInformationBaseLegalPerson?.address.zipCode,
        street: dataInformationBaseLegalPerson?.address.street,
        uf: dataInformationBaseLegalPerson?.address?.uf,
        ufCode: dataInformationBaseLegalPerson?.address?.uf
          ? UF_CODE_BY_NAME[dataInformationBaseLegalPerson?.address?.uf]
          : 0,
        latitude: dataInformationBaseLegalPerson?.address?.latitude ?? 0,
        longitude: dataInformationBaseLegalPerson.address.longitude ?? 0,
      });
      //setDocument(dataInformationBaseLegalPerson.document);
      setValue('name', dataInformationBaseLegalPerson?.fantasyName);
      setValue('postalCode', dataInformationBaseLegalPerson?.address?.zipCode);
      setValue('street', dataInformationBaseLegalPerson?.address?.street);
      setValue('number', dataInformationBaseLegalPerson?.address?.number?.toString());
      setValue('city', dataInformationBaseLegalPerson?.address?.city);
      setValue('uf', dataInformationBaseLegalPerson?.address?.uf);
    }
  }, [setValue, dataInformationBaseLegalPerson]);

  return isLoading || isSearchingCnpjData ? (
    <Container color={color} ref={refComponent}>
      <Grid display="flex" alignItems="center" justifyContent="center">
        <Typography variant="subtitle1" padding="0 0.5rem" color={theme.colors.primary}>
          Buscando...
        </Typography>
        <CircularProgress size={15} />
      </Grid>
    </Container>
  ) : (
    <Container color={color} ref={refComponent}>
      <Content className={displaySummary ? '' : 'displayNone'}>
        <div className="header">
          <div className="contentHeader">
            <div className="companyData">
              <p className="companyLabel">Empresa</p>
              <p>
                CNPJ <span>{toCnpjMask(document)}</span>
              </p>
            </div>
            <div className="containerUf">
              <Input
                name="uf"
                width="6rem"
                placeholder="Estado"
                value={address.uf ?? ''}
                control={control}
                variant="standard"
                error={formSubmited ? !!errors.uf : false}
                errorText={formSubmited ? errors.uf?.message : ''}
                endAdornment={
                  <IconButton disabled>
                    <Lock />
                  </IconButton>
                }
                disabled
              />
            </div>
          </div>
          <IconButton onClick={handleHiddenSummary}>
            <ExpandCircleDownIcon
              color={
                tradingName ||
                data.postalCode ||
                data.street ||
                data.complement ||
                data.city ||
                data.number
                  ? 'success'
                  : 'error'
              }
              fontSize="large"
              className="icon"
            />
          </IconButton>
        </div>
        <div className="body">
          <div className="containerNameCompany">
            <Input
              name="name"
              placeholder="Nome Fantasia"
              value={tradingName ?? ''}
              control={control}
              variant="standard"
              lengthInput={50}
            />
          </div>
          <div className="containerPostalCode" id="cep">
            <Input
              name="postalCode"
              className="postalCode"
              placeholder="CEP"
              value={address.postalCode?.replace(/\D/g, '') ?? ''}
              onChange={(e) => {
                handleChangePostalCode(e.target.value);
              }}
              control={control}
              variant="standard"
              lengthInput={9}
              mask={MASK_CEP}
              preventNotNumbers
              error={formSubmited ? !!errors.postalCode : false}
              errorText={formSubmited ? errors.postalCode?.message : ''}
            />
          </div>
        </div>
        <div className="footer" id="address">
          <div className="containerAddress">
            <div className="containerCity">
              <Input
                name="city"
                className="city"
                placeholder="Cidade"
                value={address.city ?? ''}
                control={control}
                variant="standard"
                lengthInput={30}
                error={formSubmited ? !!errors.city : false}
                errorText={formSubmited ? errors.city?.message : ''}
                endAdornment={
                  <IconButton disabled>
                    <Lock />
                  </IconButton>
                }
                disabled
              />
            </div>
            <div className="containerStreet">
              <Input
                name="street"
                className="street"
                placeholder="Endereço"
                value={address?.street ?? ''}
                control={control}
                variant="standard"
                lengthInput={30}
                error={formSubmited ? !!errors.street : false}
                errorText={formSubmited ? errors.street?.message : ''}
                endAdornment={
                  <IconButton disabled>
                    <Lock />
                  </IconButton>
                }
                disabled
              />
            </div>
            <div className="containerNumber">
              <Input
                name="number"
                placeholder="Número"
                value={address.number?.toString() ?? ''}
                control={control}
                variant="standard"
                lengthInput={5}
                mask={MASK_NUMBER_ADDRESS}
                preventNotNumbers
                error={formSubmited ? !!errors.number : false}
                errorText={formSubmited ? errors.number?.message : ''}
              />
            </div>
          </div>
          <div className="containerComplement">
            <Input
              name="complement"
              className="complement"
              placeholder="Complemento"
              value={address.complement ?? ''}
              control={control}
              variant="standard"
              lengthInput={30}
            />
          </div>
        </div>
      </Content>
      <ContentSummary className={displaySummary ? 'displayNone' : ''}>
        <div className="headerSummary">
          <button type="button" onClick={handleHiddenSummary}>
            Empresa
          </button>
          <IconButton onClick={handleHiddenSummary} id="expand-address">
            <ExpandCircleDownIcon
              color={
                data.name ||
                data.postalCode ||
                data.street ||
                data.complement ||
                data.city ||
                data.number
                  ? 'success'
                  : 'error'
              }
              fontSize="large"
            />
          </IconButton>
        </div>
        {data.name && (
          <div className="summary">
            <button type="button" onClick={handleHiddenSummary}>
              {data.name}
            </button>
          </div>
        )}
      </ContentSummary>
    </Container>
  );
});
